/**
 * Accept/Reject card for ``suggest_outline_changes`` proposals, in Word.
 *
 * The web app renders this card; the pane had only a tool-activity row, so a
 * "change the word counts" request produced a proposal nobody could accept
 * (PE-604 testing). Same shell, same decision memory (local storage keyed by
 * bid and proposal), same backend call. What differs: section updates (a
 * limit, a status, a reviewer) apply from here; adding, removing or
 * reordering sections still happens in mytender, because the document's
 * question blocks need a matching state for a section that no longer exists.
 */
import { type ToolCallMessagePartComponent, makeAssistantToolUI, useAuiState } from "@assistant-ui/react";
import { ListTreeIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { applyOutlineProposal } from "@/agents-v2/api/outlineProposal";
import { asRecord, getFirstStringValue, parseMaybeJson } from "@/agents-v2/utils/unknown";
import { mytenderUrl, openExternal } from "@/agents-v2/utils/open-external";

import {
  BID_METADATA_PROPOSAL_TOOL_NAME,
  OUTLINE_PROPOSAL_TOOL_NAME,
  coalesceSectionUpdateChanges,
  mergeProposalSummaries,
  metadataUpdatesToOutlineChanges,
  parseBidMetadataProposal,
  parseOutlineProposal,
} from "./bid-proposal-merge";
import {
  type OutlineProposalDecision,
  readOutlineProposalDecision,
  writeOutlineProposalDecision,
} from "./outline-proposal-decisions";
import { useProposalEnv } from "./proposal-env";
import { ProposalReviewCard, ProposalReviewErrorCard } from "./proposal-review-card";

type SiblingMetadata = {
  toolCallId: string;
  proposalId: string | null;
  summary: string;
  rationale: string | null;
  changes: ReturnType<typeof metadataUpdatesToOutlineChanges>;
};

const STRUCTURAL_OPS = new Set(["add", "delete", "reorder", "renumber"]);

const OutlineProposalRender: ToolCallMessagePartComponent<unknown, unknown> = ({ result, status, toolCallId }) => {
  const { token, bidId: linkedBidId, notify, onApplied } = useProposalEnv();
  const messageParts = useAuiState((state) => state.message.parts);

  const siblingMetadata = useMemo((): SiblingMetadata[] => {
    const siblings: SiblingMetadata[] = [];
    for (const part of messageParts) {
      if (part.type !== "tool-call") continue;
      if (part.toolName !== BID_METADATA_PROPOSAL_TOOL_NAME) continue;
      if (part.status?.type !== "complete") continue;
      const parsed = parseBidMetadataProposal(part.result);
      if (!parsed) continue;
      const sectionChanges = metadataUpdatesToOutlineChanges(parsed.updates);
      if (sectionChanges.length === 0) continue;
      siblings.push({
        toolCallId: part.toolCallId,
        proposalId: parsed.proposalId,
        summary: parsed.summary,
        rationale: parsed.rationale,
        changes: sectionChanges,
      });
    }
    return siblings;
  }, [messageParts]);

  const isComplete = status?.type === "complete";
  const proposal = useMemo(() => (isComplete ? parseOutlineProposal(result) : null), [isComplete, result]);
  const errorMessage = useMemo(() => {
    if (!isComplete || proposal) return null;
    const record = asRecord(parseMaybeJson(result));
    if (getFirstStringValue(record, ["status"]) === "error") {
      return getFirstStringValue(record, ["message", "error"]) || "Couldn't prepare outline suggestions.";
    }
    if (result == null || result === "") return "Outline proposal returned no result.";
    return "Outline proposal couldn't be shown. Try asking again.";
  }, [isComplete, proposal, result]);

  const mergedChanges = useMemo(() => {
    if (!proposal) return [];
    return coalesceSectionUpdateChanges([...proposal.changes, ...siblingMetadata.flatMap((s) => s.changes)]);
  }, [proposal, siblingMetadata]);
  const displaySummary = useMemo(
    () => (proposal ? mergeProposalSummaries([proposal.summary, ...siblingMetadata.map((s) => s.summary)]) : ""),
    [proposal, siblingMetadata]
  );
  const structural = useMemo(() => mergedChanges.filter((c) => STRUCTURAL_OPS.has(c.op)), [mergedChanges]);

  const bidId = proposal?.bidId || linkedBidId;
  const decisionKeys = useMemo(() => {
    const keys = [toolCallId, proposal?.proposalId, ...siblingMetadata.flatMap((s) => [s.toolCallId, s.proposalId])];
    return [...new Set(keys.filter((key): key is string => Boolean(key)))];
  }, [proposal?.proposalId, siblingMetadata, toolCallId]);

  const loadDecision = useCallback((): OutlineProposalDecision | null => {
    for (const key of decisionKeys) {
      const stored = readOutlineProposalDecision(bidId, key);
      if (stored) return stored;
    }
    return null;
  }, [bidId, decisionKeys]);
  const [decision, setDecision] = useState<OutlineProposalDecision | null>(() => loadDecision());
  const [busy, setBusy] = useState(false);
  useEffect(() => setDecision(loadDecision()), [loadDecision]);
  const rememberDecision = useCallback(
    (next: OutlineProposalDecision) => {
      for (const key of decisionKeys) writeOutlineProposalDecision(bidId, key, next);
      setDecision(next);
    },
    [bidId, decisionKeys]
  );

  const onAccept = useCallback(async () => {
    if (!proposal || !token || decision || busy) return;
    if (!bidId) {
      notify("This proposal names no bid; link the document to a tender first.");
      return;
    }
    if (linkedBidId && bidId !== linkedBidId) {
      notify("This proposal is for a different tender than the one this document is linked to.");
      return;
    }
    setBusy(true);
    try {
      const response = await applyOutlineProposal(bidId, mergedChanges, token, displaySummary);
      if (response.status !== "success") throw new Error(response.message || "Failed to apply outline changes");
      rememberDecision("accepted");
      notify(response.message || "Outline updated in mytender.");
      onApplied();
    } catch (error) {
      console.error(error);
      notify(error instanceof Error ? error.message : "Failed to apply outline changes");
    } finally {
      setBusy(false);
    }
  }, [bidId, busy, decision, displaySummary, linkedBidId, mergedChanges, notify, onApplied, proposal, rememberDecision, token]);

  const onReject = useCallback(() => {
    if (decision) return;
    rememberDecision("rejected");
  }, [decision, rememberDecision]);

  if (!proposal) {
    if (!errorMessage) return null;
    return (
      <ProposalReviewErrorCard
        dataSlot="outline-proposal-error"
        icon={ListTreeIcon}
        title="Outline proposal unavailable"
        message={errorMessage}
      />
    );
  }

  const changeCount = mergedChanges.length;
  const hasSiblings = siblingMetadata.length > 0;

  if (structural.length) {
    // Sections added, removed or reordered: the document's answer blocks
    // have no state for that yet, so it is done in mytender's Plan tab.
    return (
      <ProposalReviewCard
        dataSlot="outline-proposal-result"
        icon={ListTreeIcon}
        title="Outline changes proposed"
        summary={<p>{displaySummary}</p>}
        detail={
          <>
            <p>
              {structural.length} of these change{changeCount === 1 ? "" : "s"} add, remove or reorder sections, which
              is done in mytender for now.
            </p>
            <button
              type="button"
              className="underline"
              onClick={() => void openExternal(mytenderUrl(bidId, "proposal-planner"))}
            >
              Open the Plan tab in mytender
            </button>
          </>
        }
        decision={decision}
        acceptedLabel="Applied to the outline."
        busy={busy}
        onAccept={onReject}
        onReject={onReject}
      />
    );
  }

  return (
    <ProposalReviewCard
      dataSlot="outline-proposal-result"
      icon={ListTreeIcon}
      title={hasSiblings ? "Changes proposed" : "Outline changes proposed"}
      summary={<p>{displaySummary}</p>}
      detail={
        <>
          {hasSiblings ? (
            <ul className="list-disc space-y-0.5 pl-4">
              <li>{proposal.summary}</li>
              {siblingMetadata.map((sibling) => (
                <li key={sibling.toolCallId}>{sibling.summary}</li>
              ))}
            </ul>
          ) : null}
          {proposal.rationale && !hasSiblings ? <p>{proposal.rationale}</p> : null}
          <p>
            {changeCount} change{changeCount === 1 ? "" : "s"} pending
          </p>
        </>
      }
      decision={decision}
      acceptedLabel="Applied to the outline."
      busy={busy}
      onAccept={() => void onAccept()}
      onReject={onReject}
    />
  );
};

export const OutlineProposalToolUI = makeAssistantToolUI<unknown, unknown>({
  toolName: OUTLINE_PROPOSAL_TOOL_NAME,
  render: OutlineProposalRender,
});
